/**
 * The plan's facts under the title, as one row of calm tiles (user,
 * 19/10/2026: "minimalist, intuitive, clean", then "cleaner, more
 * professional"): Send window, Drop generation, Auto-generate, Auto-send and
 * the route tests, the errors (19/10/2026), each the same four rows — label, value, detail, status.
 * Times read Danish first with the country's time under them, flags
 * leading so both clocks line up; a click on a time edits it, Auto-send
 * opens the campaign groups, Route tests opens the test. Two by two on a
 * narrow screen.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { CalendarClock, Clock } from 'lucide-react';
import type { DayPlanPreview } from '@trafficpulse/shared';

import { cn } from '@/lib/utils';

import type { DropGenPlanDetail } from '../../../drop-generator.types';
import { useSchedulerStatus } from '../../../useDropGenerator';
import { minutesToHHMM } from '../../../utils/format';
import { useCountryClock } from '../../shared/time/country-clock-context';
import { AutoSendSegment } from '../autosend/AutoSendSegment';
import { ErrorsTile } from '../errors/ErrorsTile';
import { todayGeneration } from '../facts/generation-fact';
import { useMinuteTick } from '../facts/use-minute-tick';
import { windowNow } from '../facts/window-now';
import { routeTestFact } from '../route-test/route-test-fact';
import { RouteTestTile } from '../route-test/RouteTestTile';
import { AutoSegment } from './AutoSegment';
import { ClockLine, DkTime } from './DkTime';
import { FactTile, type TileAction } from './FactTile';

const WINDOW_HINT =
  "When drops may go out: a hard limit — no drop is planned outside it unless a container or a single drop lifts it. You pick it in Danish time; it is saved on the country's clock, so its Danish time moves when either country changes its clocks.";
const GENERATION_HINT =
  'Every day at this time the day is written as draft drops — which container, when, how much, on which list. Nothing is sent then: each drop goes out at its own time inside the send window. With Auto-generate off, nothing runs until someone presses Generate now.';
const NO_TZ = { text: "Set the country's timezone", tone: 'warning' as const };
/** An odd last tile fills the row on a narrow screen — no empty cell. */
const GRID = 'grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-border/60 bg-border/60 shadow-sm [&>*:last-child:nth-child(odd)]:col-span-2 lg:[&>*:last-child:nth-child(odd)]:col-span-1';

interface TileProps {
  plan: DropGenPlanDetail;
  edit: TileAction;
  now: Date;
}

/** The country's own clock under the Danish one; empty without a timezone. */
const LocalLine: FC<{ tz: string | null; mins: number[]; nextDay?: boolean }> = ({ tz, mins, nextDay = false }) => {
  const { flag } = useCountryClock();
  return tz ? <ClockLine flag={flag}>{mins.map(minutesToHHMM).join(' → ')}{nextDay ? ' +1' : ''}</ClockLine> : <>Danish time unknown</>;
};

/** "+1" after a window close past midnight (26/10/2026). */
const NextDay: FC = () => (
  <span className="ml-1 rounded-full bg-primary/10 px-1.5 align-middle font-mono text-[11px] font-semibold text-primary" title="Closes the next day — the window runs past midnight" data-testid="window-next-day">
    +1
  </span>
);

const SendWindowTile: FC<TileProps> = ({ plan, edit, now }) => {
  const { runDate } = useCountryClock();
  const tz = plan.recipientTimezone;
  const state = windowNow({ startMin: plan.windowStartMin, endMin: plan.windowEndMin, tz }, now);
  const status = state ? { text: state.text, tone: state.open ? ('live' as const) : ('muted' as const) } : NO_TZ;
  return (
    <FactTile icon={Clock} label="Send window" hint={WINDOW_HINT} action={edit} detail={<LocalLine tz={tz} mins={[plan.windowStartMin, plan.windowEndMin]} nextDay={plan.windowEndMin < plan.windowStartMin} />} status={status} testId="fact-window">
      <ClockLine flag={tz ? '🇩🇰' : null}>
        <DkTime min={plan.windowStartMin} tz={tz} runDate={runDate} /> → <DkTime min={plan.windowEndMin} tz={tz} runDate={runDate} />
        {plan.windowEndMin < plan.windowStartMin ? <NextDay /> : null}
      </ClockLine>
    </FactTile>
  );
};

const DropGenerationTile: FC<TileProps> = ({ plan, edit }) => {
  const { runDate } = useCountryClock();
  const mine = useSchedulerStatus().data?.plans.find((p) => p.countryKey === plan.countryKey);
  const tz = plan.recipientTimezone;
  return (
    <FactTile icon={CalendarClock} label="Drop generation" hint={GENERATION_HINT} action={edit} detail={<LocalLine tz={tz} mins={[plan.generateAtMin]} />} status={tz ? todayGeneration(mine) : NO_TZ} testId="fact-layout">
      <ClockLine flag={tz ? '🇩🇰' : null}>
        <DkTime min={plan.generateAtMin} tz={tz} runDate={runDate} />
      </ClockLine>
    </FactTile>
  );
};

export const PlanFactsBar: FC<{
  plan: DropGenPlanDetail;
  preview?: DayPlanPreview;
  onEditTimes: () => void;
  groupsOpen: boolean;
  onToggleGroups: () => void;
  /** Opens a container's editor (the route test). */
  onOpenContainer?: (id: string) => void;
  /** Opens the Errors tab. */
  onOpenErrors?: () => void;
}> = ({ plan, preview, onEditTimes, groupsOpen, onToggleGroups, onOpenContainer, onOpenErrors }) => {
  const now = useMinuteTick();
  const { runDate } = useCountryClock();
  const edit = { label: 'Edit the send window and the drop generation time', onClick: onEditTimes };
  const routeTest = routeTestFact(plan, preview, runDate);
  return (
    <div className={cn(GRID, routeTest ? 'lg:grid-cols-6' : 'lg:grid-cols-5')}>
      {/* Route tests first (26/10/2026, user: "move the route test as the first one … on the left") — they go before the window opens. */}
      {routeTest ? <RouteTestTile fact={routeTest} tz={plan.recipientTimezone} onOpen={onOpenContainer} /> : null}
      <SendWindowTile plan={plan} edit={edit} now={now} />
      <DropGenerationTile plan={plan} edit={edit} now={now} />
      <AutoSegment plan={plan} now={now} />
      <AutoSendSegment plan={plan} open={groupsOpen} onToggle={onToggleGroups} />
      <ErrorsTile countryKey={plan.countryKey} onOpen={onOpenErrors} />
    </div>
  );
};
